/**
 * Сборка пространства из того, что помнит аккаунт.
 *
 * До этого аккаунт не возвращал ничего. Итоги занятий уходили на сервер и
 * оставались там: на другом устройстве человек видел пустое пространство, а
 * очистка браузера уносила всю работу, хотя она лежала в базе. Вход в аккаунт
 * означал только «сервер знает, кто вы», но не «продолжайте оттуда, где
 * остановились» (задание §15 — «получение прогресса и слабых зон»).
 *
 * Восстанавливается измеренное, а не выведенное: состояние каждой единицы
 * приходит таким, каким его отправил клиент. Того, чего аккаунт не помнит,
 * здесь не выдумывают — см. ниже про раскладку и путь обучения.
 */

import type {
  InstallationId,
  LanguageCode,
  MasteryStat,
  MasteryUnit,
  MasteryUnitKind,
  UserId,
} from '@type2x/shared-contracts';
import { MASTERY_UNIT_KINDS, toMasteryUnitId } from '@type2x/shared-contracts';
import type { RestoredProfile } from '@type2x/feature-sync';
import {
  createLanguageProfile,
  createWorkspace,
  type LanguageProfile,
  type Workspace,
} from '@type2x/feature-personal-workspace';

/** Виды единиц, известные приложению. */
const KINDS = new Set<string>(MASTERY_UNIT_KINDS);

/**
 * Учебная единица, восстановленная из идентификатора.
 *
 * Идентификатор устроен как `вид:содержание`, и этого хватает: единица — не
 * пользовательские данные, а материал языка. `null` — вид неизвестен; такие
 * идентификаторы есть (например, контрольный пул), и придумывать им вид
 * незачем: статистика по ним сохраняется, а сама единица вернётся в
 * пространство, когда её создаст режим.
 */
function toUnit(unitId: string, language: LanguageCode, layoutId: string): MasteryUnit | null {
  const separator = unitId.indexOf(':');
  if (separator <= 0) return null;

  const kind = unitId.slice(0, separator);
  if (!KINDS.has(kind)) return null;

  return {
    id: toMasteryUnitId(unitId),
    kind: kind as MasteryUnitKind,
    content: unitId.slice(separator + 1),
    language,
    layoutId: layoutId as MasteryUnit['layoutId'],
    // Частота в языке — свойство материала, а не человека, и аккаунт её не
    // хранит. Ноль здесь означает «неизвестна»: первое же занятие перезапишет
    // единицу настоящей (`upsertMastery`), как это уже происходит с
    // единицами пакетов.
    realWorldFrequency: 0,
  };
}

/** Профиль языка из того, что вернул аккаунт. */
function toProfile(restored: RestoredProfile, now: string): LanguageProfile {
  const units: Record<string, MasteryUnit> = {};
  const stats: Record<string, MasteryStat> = {};

  for (const unit of restored.units) {
    const rebuilt = toUnit(unit.unitId, restored.language, unit.layoutId);
    if (rebuilt !== null) units[rebuilt.id] = rebuilt;

    stats[unit.unitId] = {
      unitId: toMasteryUnitId(unit.unitId),
      attempts: unit.attempts,
      firstTryCorrect: unit.firstTryCorrect,
      errorRate: unit.errorRate,
      medianLatencyMs: unit.medianLatencyMs,
      p95LatencyMs: unit.p95LatencyMs,
      correctionRate: unit.correctionRate,
      masteryScore: unit.masteryScore,
      consecutiveSuccesses: unit.consecutiveSuccesses,
      lastSeenAt: unit.lastSeenAt,
      nextReviewAt: unit.nextReviewAt,
      layoutId: unit.layoutId,
      language: restored.language,
    };
  }

  /*
   * Операционная система и форма клавиатуры аккаунту неизвестны: он хранит
   * раскладку, а не устройство, на котором её набирают. Берутся значения по
   * умолчанию — их видно в настройках, и человек поправит их одним нажатием,
   * если новое устройство другое.
   *
   * Путь обучения («с основ» или «с тренировки») тоже не хранится, и остаётся
   * значение по умолчанию: восстановленный навык всё равно ведёт в
   * персональную тренировку, а «Основы» открыты из каталога.
   */
  const base = createLanguageProfile(restored.language, {
    layoutId: restored.layoutId,
    platform: 'windows',
    form: 'ansi',
  });

  return {
    ...base,
    baseline:
      restored.baseline === null
        ? null
        : {
            language: restored.language,
            layoutId: restored.layoutId,
            wpm: restored.baseline.wpm,
            cpm: restored.baseline.cpm,
            accuracy: restored.baseline.accuracy,
            // Когда его измерили, аккаунт не помнит. Метка календарная и на
            // расчёт роста не влияет — рост считается от самих чисел.
            measuredAt: now,
          },
    bestVerifiedWpm: restored.bestVerifiedWpm,
    units,
    stats,
  };
}

/**
 * Собирает пространство из прогресса аккаунта; `null` — восстанавливать нечего.
 *
 * Пустой ответ сервера и его молчание здесь уже неразличимы — и то и другое
 * означает, что человек начинает с чистого места.
 */
export function restoreWorkspace(
  profiles: readonly RestoredProfile[],
  userId: UserId,
  installationId: InstallationId,
  uiLanguage: LanguageCode,
  now: string,
): Workspace | null {
  if (profiles.length === 0) return null;

  const restored: Record<string, LanguageProfile> = {};
  for (const profile of profiles) restored[profile.language] = toProfile(profile, now);

  // Язык тренировки — язык интерфейса, если человек им занимался; иначе
  // первый из вернувшихся. Пустым активный язык остаться не может: профили
  // есть, и какой-то из них открывается.
  const active = restored[uiLanguage] === undefined ? profiles[0]!.language : uiLanguage;

  return {
    ...createWorkspace({ kind: 'user', userId, installationId }, uiLanguage, now),
    activeLanguage: active,
    profiles: restored,
  };
}
